import { Locales } from '@griply/types';

import {
  Gender,
  getDayGender,
  getDayRenderForms,
  getOnForm,
  getOrdinalForm,
  getOrdinalSuffix,
  OrdinalPosition,
} from './day-render-forms';
import { getMonthMap, getRruleKeywords } from './locale-vocabulary';
import { getUnitForm, RruleUnit } from './unit-forms';

export type RruleParts = Record<string, string>;

export type RruleFormatContext = {
  k: Record<string, string>;
  language: Locales;
  dayForms: Record<string, string>;
  monthNames: Record<number, string>;
  theArticle: string;
};

const ORDINAL_POSITIONS: Record<string, OrdinalPosition> = {
  '+1': OrdinalPosition.First,
  '+2': OrdinalPosition.Second,
  '+3': OrdinalPosition.Third,
  '+4': OrdinalPosition.Fourth,
  '+5': OrdinalPosition.Fifth,
  '-1': OrdinalPosition.Last,
};

const WEEKDAY_BYDAY = ['MO', 'TU', 'WE', 'TH', 'FR'].sort().join(',');
const WEEKEND_BYDAY = 'SA,SU';

export const buildFormatContext = (language: Locales): RruleFormatContext => {
  const k = getRruleKeywords(language);

  return {
    k,
    language,
    dayForms: getDayRenderForms(language),
    monthNames: Object.entries(getMonthMap(language)).reduce<
      Record<number, string>
    >((acc, [name, index]) => {
      if (!acc[index] || name.length < acc[index].length) {
        acc[index] = name;
      }
      return acc;
    }, {}),
    theArticle: k.the_simple ? `${k.the_simple} ` : '',
  };
};

const onFor = (ctx: RruleFormatContext, dayCode?: string) =>
  getOnForm(
    dayCode ? getDayGender(dayCode, ctx.language) : Gender.Masculine,
    ctx.language,
  );

// The ordinal agrees with the noun that follows it, so the weekday decides its gender.
const ordinalFor = (
  value: string,
  ctx: RruleFormatContext,
  dayCode?: string,
) => {
  const position = ORDINAL_POSITIONS[value];
  if (!position) {
    return undefined;
  }

  return getOrdinalForm(
    position,
    dayCode ? getDayGender(dayCode, ctx.language) : Gender.Masculine,
    ctx.language,
  );
};

// "2 months" / "month" — the count prefix is dropped for a plain interval of one.
const unitPhrase = (
  unit: RruleUnit,
  interval: string | undefined,
  ctx: RruleFormatContext,
) => {
  const count = interval ? parseInt(interval, 10) : 1;
  const term = getUnitForm(unit, count, ctx.language);
  return count > 1 ? `${count} ${term}` : term;
};

const dayGroupTerm = (byDay: string, ctx: RruleFormatContext) => {
  const normalized = byDay
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .sort()
    .join(',');
  if (normalized === WEEKEND_BYDAY) {
    return ctx.k.weekend_simple;
  }
  return normalized === WEEKDAY_BYDAY ? ctx.k.weekday_simple : null;
};

const ordinalKeyOf = (bySetPos: string) => {
  const setpos = parseInt(bySetPos, 10);
  return setpos > 0 ? `+${setpos}` : `${setpos}`;
};

const splitOrdinalDay = (byDay: string) => {
  const match = byDay.match(/([+-]?\d+)([A-Z]{2})/);
  return match ? { ordinal: match[1], dayCode: match[2] } : null;
};

// "every 2 weeks on fri"
const weeklyIntervalOnDays = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY, INTERVAL } = parts;
  if (FREQ !== 'WEEKLY' || !INTERVAL || !BYDAY || parseInt(INTERVAL, 10) <= 1) {
    return null;
  }

  const days = BYDAY.split(',')
    .map((d) => ctx.dayForms[d.trim()])
    .filter(Boolean)
    .join(', ');
  if (!days) {
    return null;
  }

  return `${ctx.k.every_simple} ${unitPhrase(RruleUnit.Week, INTERVAL, ctx)} ${onFor(ctx, BYDAY.split(',')[0].trim())} ${days}`;
};

// "every month on the first weekend" / "... on the second monday"
const monthlyOnSetPos = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY, INTERVAL, BYSETPOS } = parts;
  if (FREQ !== 'MONTHLY' || !BYDAY || !BYSETPOS) {
    return null;
  }

  const ordinalKey = ordinalKeyOf(BYSETPOS);
  if (!ordinalFor(ordinalKey, ctx)) {
    return null;
  }

  const bydayTokens = BYDAY.split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .sort();
  const monthTerm = unitPhrase(RruleUnit.Month, INTERVAL, ctx);

  if (bydayTokens.length === 1 && ctx.dayForms[bydayTokens[0]]) {
    const ordinal = ordinalFor(ordinalKey, ctx, bydayTokens[0]);
    // eslint-disable-next-line max-len -- long output line
    return `${ctx.k.every_simple} ${monthTerm} ${onFor(ctx, bydayTokens[0])} ${ctx.theArticle}${ordinal} ${ctx.dayForms[bydayTokens[0]]}`;
  }

  const dayTerm = dayGroupTerm(BYDAY, ctx);
  if (!dayTerm) {
    return null;
  }

  // eslint-disable-next-line max-len -- long output line
  return `${ctx.k.every_simple} ${monthTerm} ${ctx.k.on_simple} ${ctx.theArticle}${ordinalFor(ordinalKey, ctx)} ${dayTerm}`;
};

// "every 2 months on the first monday" (BYDAY=+1MO)
const monthlyOnOrdinalDay = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY, INTERVAL } = parts;
  if (FREQ !== 'MONTHLY' || !BYDAY) {
    return null;
  }

  const split = splitOrdinalDay(BYDAY);
  if (!split) {
    return null;
  }

  const ordinal = ordinalFor(split.ordinal, ctx, split.dayCode);
  const dayWord = ctx.dayForms[split.dayCode];
  if (!ordinal || !dayWord) {
    return null;
  }

  // eslint-disable-next-line max-len -- long output line
  return `${ctx.k.every_simple} ${unitPhrase(RruleUnit.Month, INTERVAL, ctx)} ${onFor(ctx, split.dayCode)} ${ctx.theArticle}${ordinal} ${dayWord}`;
};

// "every month on the last day" (BYMONTHDAY=-1)
const monthlyOnLastDay = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY, INTERVAL, BYMONTHDAY } = parts;
  if (FREQ !== 'MONTHLY' || BYMONTHDAY !== '-1' || BYDAY) {
    return null;
  }

  const lastOrdinal = getOrdinalForm(
    OrdinalPosition.Last,
    Gender.Masculine,
    ctx.language,
  );
  // eslint-disable-next-line max-len -- long output line
  return `${ctx.k.every_simple} ${unitPhrase(RruleUnit.Month, INTERVAL, ctx)} ${ctx.k.on_simple} ${ctx.theArticle}${lastOrdinal} ${ctx.k.day}`;
};

// "every 8th" / "every 2 months on the 8th"
const monthlyOnDayNumber = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY, INTERVAL, BYMONTHDAY } = parts;
  if (FREQ !== 'MONTHLY' || !BYMONTHDAY || BYDAY) {
    return null;
  }

  const days = BYMONTHDAY.split(',')
    .map((d) => d + getOrdinalSuffix(d, ctx.language))
    .join(', ');

  if (INTERVAL && parseInt(INTERVAL, 10) > 1) {
    // eslint-disable-next-line max-len -- long output line
    return `${ctx.k.every_simple} ${unitPhrase(RruleUnit.Month, INTERVAL, ctx)} ${ctx.k.on_simple} ${ctx.theArticle}${days}`;
  }
  return `${ctx.k.every_simple} ${days}`;
};

// "every 2 years on the first monday"
const yearlyOnOrdinalDay = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY, INTERVAL, BYMONTH } = parts;
  if (FREQ !== 'YEARLY' || !BYDAY || BYMONTH) {
    return null;
  }

  const split = splitOrdinalDay(BYDAY);
  if (!split) {
    return null;
  }

  const ordinal = ordinalFor(split.ordinal, ctx, split.dayCode);
  const dayWord = ctx.dayForms[split.dayCode];
  if (!ordinal || !dayWord) {
    return null;
  }

  // eslint-disable-next-line max-len -- long output line
  return `${ctx.k.every_simple} ${unitPhrase(RruleUnit.Year, INTERVAL, ctx)} ${onFor(ctx, split.dayCode)} ${ctx.theArticle}${ordinal} ${dayWord}`;
};

// "every year on the first Monday in February"
const yearlyOnOrdinalDayInMonth = (
  parts: RruleParts,
  ctx: RruleFormatContext,
) => {
  const { FREQ, BYDAY, INTERVAL, BYMONTH, BYSETPOS, BYMONTHDAY } = parts;
  if (FREQ !== 'YEARLY' || !BYDAY || !BYMONTH || BYSETPOS || BYMONTHDAY) {
    return null;
  }

  const split = splitOrdinalDay(BYDAY);
  const monthName = ctx.monthNames[parseInt(BYMONTH, 10) - 1];
  if (!split || !monthName) {
    return null;
  }

  const ordinal = ordinalFor(split.ordinal, ctx, split.dayCode);
  const dayWord = ctx.dayForms[split.dayCode];
  if (!ordinal || !dayWord) {
    return null;
  }

  // eslint-disable-next-line max-len -- long output line
  return `${ctx.k.every_simple} ${unitPhrase(RruleUnit.Year, INTERVAL, ctx)} ${onFor(ctx, split.dayCode)} ${ctx.theArticle}${ordinal} ${dayWord} ${ctx.k.in_simple} ${monthName}`;
};

// "every year on the fifth weekend in January"
const yearlyOnSetPosInMonth = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY, INTERVAL, BYMONTH, BYSETPOS, BYMONTHDAY } = parts;
  if (FREQ !== 'YEARLY' || !BYDAY || !BYMONTH || !BYSETPOS || BYMONTHDAY) {
    return null;
  }

  const ordinalKey = ordinalKeyOf(BYSETPOS);
  const monthName = ctx.monthNames[parseInt(BYMONTH, 10) - 1];
  if (!ordinalFor(ordinalKey, ctx) || !monthName) {
    return null;
  }

  const normalized = BYDAY.split(',')
    .map((s) => s.trim())
    .sort()
    .join(',');
  const groupTerm = dayGroupTerm(BYDAY, ctx);
  const dayCode = groupTerm ? undefined : normalized;
  const dayTerm = groupTerm ?? ctx.dayForms[normalized];
  if (!dayTerm) {
    return null;
  }

  // eslint-disable-next-line max-len -- long output line
  return `${ctx.k.every_simple} ${unitPhrase(RruleUnit.Year, INTERVAL, ctx)} ${onFor(ctx, dayCode)} ${ctx.theArticle}${ordinalFor(ordinalKey, ctx, dayCode)} ${dayTerm} ${ctx.k.in_simple} ${monthName}`;
};

// "every jan 28"
const yearlyOnMonthDay = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, INTERVAL, BYMONTH, BYMONTHDAY } = parts;
  if (FREQ !== 'YEARLY' || !BYMONTH || !BYMONTHDAY) {
    return null;
  }

  const monthName = ctx.monthNames[parseInt(BYMONTH, 10) - 1];
  if (!monthName) {
    return null;
  }

  if (INTERVAL && parseInt(INTERVAL, 10) > 1) {
    // eslint-disable-next-line max-len -- long output line
    return `${ctx.k.every_simple} ${unitPhrase(RruleUnit.Year, INTERVAL, ctx)} ${ctx.k.on_simple} ${monthName} ${BYMONTHDAY}`;
  }
  return `${ctx.k.every_simple} ${monthName} ${BYMONTHDAY}`;
};

const FREQ_UNITS: Record<string, RruleUnit> = {
  DAILY: RruleUnit.Day,
  WEEKLY: RruleUnit.Week,
  MONTHLY: RruleUnit.Month,
  YEARLY: RruleUnit.Year,
};

// "every 3 days"
const bareInterval = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, INTERVAL } = parts;
  if (!INTERVAL || parseInt(INTERVAL, 10) <= 1 || !FREQ_UNITS[FREQ]) {
    return null;
  }

  return `${ctx.k.every_simple} ${unitPhrase(FREQ_UNITS[FREQ], INTERVAL, ctx)}`;
};

// "every monday, friday"
const onDays = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY } = parts;
  if ((FREQ !== 'DAILY' && FREQ !== 'WEEKLY') || !BYDAY) {
    return null;
  }

  const days = BYDAY.split(',')
    .map((d) => ctx.dayForms[d.trim()])
    .filter(Boolean)
    .join(', ');
  return days ? `${ctx.k.every_simple} ${days}` : null;
};

const FREQ_KEYWORDS: Record<string, string> = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
};

const bareFrequency = (parts: RruleParts, ctx: RruleFormatContext) => {
  const { FREQ, BYDAY } = parts;
  if (BYDAY || !FREQ_KEYWORDS[FREQ]) {
    return null;
  }
  return ctx.k[FREQ_KEYWORDS[FREQ]];
};

// Most specific first; the caller falls back to the raw rrule when every one declines.
export const RRULE_FORMATTERS: ((
  parts: RruleParts,
  ctx: RruleFormatContext,
) => string | null)[] = [
  weeklyIntervalOnDays,
  monthlyOnSetPos,
  monthlyOnOrdinalDay,
  monthlyOnLastDay,
  monthlyOnDayNumber,
  yearlyOnOrdinalDay,
  yearlyOnOrdinalDayInMonth,
  yearlyOnSetPosInMonth,
  yearlyOnMonthDay,
  bareInterval,
  onDays,
  bareFrequency,
];
